import React, { createContext, useState, useContext } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle 
} from '@mui/material';

const ConfirmationContext = createContext();

export const useConfirmation = () => useContext(ConfirmationContext);

export const ConfirmationProvider = ({ children }) => {
  const [confirmationState, setConfirmationState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Yes',
    cancelText: 'No',
    confirmButtonColor: 'primary',
    showOnlyOk: false, // New state for controlling OK button visibility
    onConfirm: null,
    onCancel: null,
  });

  const showConfirmation = (options = {}) => {
    return new Promise((resolve) => {
      setConfirmationState({
        isOpen: true,
        title: options.title || 'Confirmation',
        message: options.message || 'Are you sure you want to proceed?',
        confirmText: options.confirmText || (options.showOnlyOk ? 'OK' : 'Yes'), // Default to 'OK' if showOnlyOk is true
        cancelText: options.cancelText || 'No',
        confirmButtonColor: options.confirmButtonColor || 'primary',
        showOnlyOk: options.showOnlyOk || false, // New option to control OK button
        onConfirm: () => {
          setConfirmationState(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirmationState(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        },
      });
    });
  };

  const handleClose = () => {
    if (confirmationState.onCancel) {
      confirmationState.onCancel();
    }
    setConfirmationState(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <ConfirmationContext.Provider value={showConfirmation}>
      {children}
      
      <Dialog
        open={confirmationState.isOpen}
        onClose={handleClose}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">
          {confirmationState.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">
            {confirmationState.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirmationState.showOnlyOk ? (
            <Button 
              onClick={handleClose} 
              color={confirmationState.confirmButtonColor} 
              variant="contained" 
              autoFocus
            >
              {confirmationState.confirmText}
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} color="inherit">
                {confirmationState.cancelText}
              </Button>
              <Button 
                onClick={confirmationState.onConfirm} 
                color={confirmationState.confirmButtonColor} 
                variant="contained" 
                autoFocus
              >
                {confirmationState.confirmText}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </ConfirmationContext.Provider>
  );
};

export default ConfirmationProvider;